import {
  Autocomplete,
  Button,
  DialogProps,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { DateTimePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Check } from 'untitledui-js-base';
import { handleEnterAsTab } from '../../utils/handleEnterAsTab';
import { BoxWithHeader } from '../common';
import { BasicDialog } from '../common/BasicDialog';
import { Accident, AccidentInsuranceList, PayoutType, RegistryNameLabel } from '../types/accident';
import { ACCIDENT_REPORTING_OPTIONS, ACCIDENT_SOURCE_TYPE } from './constants';

const filter = createFilterOptions<string>();

interface AddNewInsuranceDialogProps extends Omit<DialogProps, 'onSubmit'> {
  onSubmit?: (insuranceTitle: string) => Promise<void>;
  isLoading?: boolean;
  defaultValue?: string;
}

const AddNewInsuranceDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  defaultValue = ''
}: AddNewInsuranceDialogProps) => {
  const [insurance, setInsurance] = useState(defaultValue);

  useEffect(() => {
    setInsurance(defaultValue);
  }, [defaultValue]);

  return (
    <BasicDialog open={open} onClose={onClose} title="Přidat pojišťovnu">
      <Stack>
        <TextField
          value={insurance}
          onChange={(event) => setInsurance(event.target.value)}
          label="Název pojišťovny"
          color="secondary"
          fullWidth
          InputLabelProps={{ shrink: true }}
          onKeyDown={handleEnterAsTab}
        />
        <Stack direction="row" gap={2} alignItems="center" mt={1}>
          <Button onClick={(event) => onClose?.(event, 'backdropClick')}>Zavřít</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Check size="1rem" />}
            size="large"
            onClick={async () => {
              if (!insurance) return;
              await onSubmit?.(insurance);
              onClose?.({}, 'backdropClick');
            }}
            disabled={!insurance}
            loading={isLoading}
          >
            Přidat pojišťovnu
          </Button>
        </Stack>
      </Stack>
    </BasicDialog>
  );
};

interface AccidentCreatingStepInformationsProps {
  onSubmit: (data: Accident) => void;
  formRef: React.RefObject<HTMLFormElement>;
  accident?: Accident;
  insurances?: AccidentInsuranceList[];
  insuranceTypes?: RegistryNameLabel[];
  onInsuranceAdd?: (title: string) => Promise<{ id: number; title: string } | undefined>;
  isAdditingInsurance?: boolean;
}

export const AccidentCreatingStepAccidentInformations = ({
  onSubmit,
  formRef,
  accident,
  insurances,
  insuranceTypes,
  onInsuranceAdd,
  isAdditingInsurance
}: AccidentCreatingStepInformationsProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newInsuranceTitle, setNewInsuranceTitle] = useState<string | null>(null);
  const [selectedAfterRefetch, setSelectedAfterRefetch] = useState<string | null>(null);
  const [commissionType, setCommissionType] = useState<string>('Kč');
  const commissionParsed = React.useRef(false);

  const getFormValues = (src?: Accident) => ({
    accidentReporting: {
      type: src?.accidentReporting?.type ?? ''
    },
    place: src?.place ?? '',
    date: src?.date ?? '',
    note: src?.note ?? '',
    carDamageNote: src?.carDamageNote ?? '',
    internalNote: src?.internalNote ?? '',
    insuranceType: src?.insuranceType ?? '',
    insuranceCode: src?.insuranceCode ?? '',
    insurance: {
      id: src?.insurance?.id
    },
    isPoliceHandled: src?.isPoliceHandled ?? 0,
    hasParking: src?.hasParking ?? false,
    hasTowing: src?.hasTowing ?? false,
    hasPhotos: src?.hasPhotos ?? false,
    payoutType: src?.payoutType,
    payoutInfo: src?.payoutInfo ?? '',
    source: src?.source
  });

  const methods = useForm<Accident>({
    defaultValues: getFormValues(accident)
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue
  } = methods;

  useEffect(() => {
    if (accident) {
      reset(getFormValues(accident), { keepDirtyValues: true });
    }
  }, [accident]);

  useEffect(() => {
    if (selectedAfterRefetch && insurances?.length) {
      const match = insurances.find((i) => i.title === selectedAfterRefetch);
      if (match) {
        setValue('insurance.id', match.id);
        setSelectedAfterRefetch(null);
      }
    }
  }, [insurances, selectedAfterRefetch, setValue]);

  useEffect(() => {
    if (!accident?.source?.commissionValue || commissionParsed.current) return;
    commissionParsed.current = true;
    const rawValue = accident.source.commissionValue;

    const match = rawValue.match(/^(\d+(?:[.,]\d+)?)(\s*%)?$/);

    if (match) {
      const [, value, percent] = match;
      setCommissionType(percent ? '%' : 'Kč');
      setValue('source.commissionValue', value.replace(',', '.'));
    } else {
      setCommissionType('Kč');
      setValue('source.commissionValue', rawValue.replace(/[^0-9.,]/g, ''));
    }
  }, [accident]);

  const handleAutocompleteChange = (_: React.SyntheticEvent, value: string | null) => {
    if (!value) return;

    if (value.startsWith('Přidat "') && value.endsWith('"')) {
      const actual = value.slice(8, -1);
      setNewInsuranceTitle(actual);
      setDialogOpen(true);
    } else {
      const selected = insurances?.find((i) => i.title === value);
      if (selected) {
        setValue('insurance.id', selected.id);
      }
    }
  };

  const handleAddInsurance = async (title: string) => {
    if (!onInsuranceAdd) return;
    const added = await onInsuranceAdd(title);
    if (added) {
      setSelectedAfterRefetch(added.title);
    }
  };

  const handleSubmitForm = (data: Accident) => {
    const updatedData = {
      ...data,
      ...(data.source && {
        source: {
          ...data.source,
          commissionValue: `${data.source.commissionValue}${commissionType}`
        }
      })
    };

    onSubmit(updatedData);
  };

  return (
    <>
      <BoxWithHeader title="Údaje o nehodě" mt={2} style={{ height: 'auto' }}>
        <form onSubmit={handleSubmit(handleSubmitForm)} ref={formRef}>
          <Grid container spacing={2} mt={1}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack gap={2}>
                <Controller
                  control={control}
                  name="place"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      label="Místo nehody"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.place)}
                      helperText={errors.place?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="date"
                  render={({ field: { onChange, value } }) => (
                    <DateTimePicker
                      label="Datum nehody"
                      value={value ? parseISO(value) : null}
                      onChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd HH:mm') : '')}
                      format="dd.MM.yyyy HH:mm"
                      slotProps={{
                        textField: {
                          error: Boolean(errors.date),
                          helperText: errors.date?.message
                        }
                      }}
                    />
                  )}
                />
                <FormControl fullWidth>
                  <InputLabel id="insurance-type-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Druh pojištění
                  </InputLabel>
                  <Controller
                    control={control}
                    name="insuranceType"
                    render={({ field }) => (
                      <Select {...field} labelId="insurance-type-label">
                        {insuranceTypes?.map((type) => (
                          <MenuItem key={type.name} value={type.name}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="insurance.id"
                    render={({ field: { value } }) => (
                      <Autocomplete
                        freeSolo
                        options={insurances?.map((i) => i.title) ?? []}
                        value={insurances?.find((i) => i.id === value)?.title ?? ''}
                        onChange={handleAutocompleteChange}
                        renderInput={(params) => <TextField {...params} label="Pojišťovna" />}
                        filterOptions={(options, params) => {
                          const filtered = filter(options, params);
                          const inputValue = params.inputValue.trim();
                          const isExisting = options.includes(inputValue);
                          if (inputValue && !isExisting) {
                            filtered.push(`Přidat "${inputValue}"`);
                          }
                          return filtered;
                        }}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="insurance-report-label" sx={{ backgroundColor: 'white', px: 0.5 }} required>
                    Hlášení na pojišťovnu
                  </InputLabel>
                  <Controller
                    control={control}
                    name="accidentReporting.type"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select {...field} labelId="insurance-report-label" required>
                        {ACCIDENT_REPORTING_OPTIONS?.map((type) => (
                          <MenuItem key={type.type} value={type.type}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <Controller
                  control={control}
                  name="insuranceCode"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="Číslo pojistné události"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.insuranceCode)}
                      helperText={errors.insuranceCode?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <FormControl fullWidth>
                  <InputLabel id="payoutType-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Kde poslat peníze
                  </InputLabel>
                  <Controller
                    control={control}
                    name="payoutType"
                    render={({ field }) => (
                      <Select {...field} labelId="insurance-type-label">
                        <MenuItem value={PayoutType.BANK_ACCOUNT}>Bankovní účet</MenuItem>
                        <MenuItem value={PayoutType.ADDRESS}>Adresa trvalého bydliště</MenuItem>
                        <MenuItem value={PayoutType.INVOICE}>Na fakturu</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
                <Controller
                  control={control}
                  name="payoutInfo"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="č.ú. / adresa trvalého bydliště"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.payoutInfo)}
                      helperText={errors.payoutInfo?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />

                {accident?.source?.type === ACCIDENT_SOURCE_TYPE.TIP && (
                  <Grid container spacing={2}>
                    <Grid size={9}>
                      <Controller
                        control={control}
                        name="source.commissionValue"
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            value={value}
                            onChange={onChange}
                            label="Provize tipaři"
                            color="secondary"
                            fullWidth
                            error={Boolean(errors.source?.commissionValue)}
                            helperText={errors.source?.commissionValue?.message}
                            InputLabelProps={{ shrink: true }}
                            onKeyDown={handleEnterAsTab}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormControl fullWidth>
                        <InputLabel
                          id="commissionType-label"
                          sx={{
                            backgroundColor: 'white',
                            px: 0.5
                          }}
                        >
                          Typ provize
                        </InputLabel>
                        <Select
                          value={commissionType}
                          onChange={(event) => setCommissionType(event.target.value)}
                          labelId="commissionType-label"
                        >
                          <MenuItem value="Kč">Kč</MenuItem>
                          <MenuItem value="%">%</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}

                <Controller
                  control={control}
                  name="isPoliceHandled"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />
                      }
                      label="Řešeno policií"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="hasParking"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />
                      }
                      label="Parkovné"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="hasTowing"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />
                      }
                      label="Odtah"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="hasPhotos"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />
                      }
                      label="Byla pořízena fotodokumentace"
                    />
                  )}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={3}>
                <Controller
                  control={control}
                  name="note"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Popis nehody"
                      color="secondary"
                      multiline
                      rows={5}
                      fullWidth
                      error={Boolean(errors.note)}
                      helperText={errors.note?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="carDamageNote"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Poškození vozidla"
                      color="secondary"
                      multiline
                      rows={5}
                      fullWidth
                      error={Boolean(errors.carDamageNote)}
                      helperText={errors.carDamageNote?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="internalNote"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Interní poznámka"
                      color="secondary"
                      multiline
                      rows={5}
                      fullWidth
                      error={Boolean(errors.internalNote)}
                      helperText={errors.internalNote?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
              </Stack>
            </Grid>
          </Grid>
        </form>
      </BoxWithHeader>

      <AddNewInsuranceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleAddInsurance}
        isLoading={isAdditingInsurance}
        defaultValue={newInsuranceTitle ?? ''}
      />
    </>
  );
};
